import { useMemo, useState } from 'react';
import Media from 'react-media';

import { ImageHeader, LastUpdated } from '@/components/Header';
import Breadcrumb from '@/components/Breadcrumb';
import type { BreadcrumbItem } from '@/components/Breadcrumb';
import { getGridViewCoverFromImageUrl } from '@/utilities/coverImages';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import {
  Wrapper,
  TextHeader,
  DescriptionWrapper,
  Avatar,
  Content,
  ShowMoreButton,
} from './styled';
import {
  Grid,
  Heading,
  Bones,
  Text,
  Button,
  useTheme,
} from '@simplywallst/ui-core';
import sanitize from 'sanitize-html';
import { useTracking } from 'react-tracking';

const COVER_IMG_WIDTH = 1152;
const COVER_IMG_HEIGHT = 242;

interface Props {
  isFetching?: boolean;
  imageUrl?: string | null;
  title?: string;
  author?: string;
  authorUrl?: string;
  avatar?: string;
  innerHTML?: string;
  breadCrumbs?: BreadcrumbItem[];
}

const Description = ({ safeHTML }: { safeHTML: string }) => {
  const { trackEvent } = useTracking();
  const [showDescription, setShowDescription] = useState(false);
  return (
    <>
      <Content open={showDescription}>
        <span dangerouslySetInnerHTML={{ __html: safeHTML }} />
      </Content>
      <ShowMoreButton
        styleType="softLink"
        onClick={() => {
          trackEvent({
            type: 'track',
            modifier: 'show more',
            action: 'click',
          });
          setShowDescription(!showDescription);
        }}
      >
        Show {showDescription ? 'less' : 'more'}
      </ShowMoreButton>
    </>
  );
};

const Header = ({
  imageUrl = null,
  title = '',
  author = '',
  authorUrl = '',
  avatar,
  innerHTML = '',
  breadCrumbs = [{ name: 'Investing Ideas' }],
  isFetching = true,
}: Props) => {
  const theme = useTheme();
  const { Track } = useTracking({
    type: 'track',
    subject: 'community-idea-header',
    title,
  });
  const safeHTML = useMemo(() => sanitize(innerHTML), [innerHTML]);
  const coverImageSrc = useCloudflareImage({
    src: getGridViewCoverFromImageUrl(imageUrl),
    width: COVER_IMG_WIDTH,
    height: COVER_IMG_HEIGHT,
    fit: 'cover',
    quality: 30,
  });
  return (
    <Track>
      <Wrapper>
        <ImageHeader
          alt={title}
          imageSource={coverImageSrc}
          width={COVER_IMG_WIDTH}
          height={COVER_IMG_HEIGHT}
        >
          <TextHeader
            templateRows="auto auto auto"
            data-cy-id="community-idea-text-header"
          >
            <Breadcrumb list={breadCrumbs} />
            <Heading as="h1" typography="megaStrong">
              {isFetching ? (
                <Media query={{ maxWidth: theme.mdMaxRaw }}>
                  {(matches) =>
                    matches ? (
                      <Bones maxWidth={500} minHeight={18} marginY="x0_75" />
                    ) : (
                      <Bones maxWidth={500} minHeight={21} marginY="x1" />
                    )
                  }
                </Media>
              ) : (
                title
              )}
            </Heading>
            <Grid
              columnGap="16px"
              templateColumns="auto auto"
              justifyContent="start"
              alignItems="center"
            >
              <Grid
                columnGap="6px"
                templateColumns="auto auto"
                justifyContent="start"
                alignItems="center"
              >
                {avatar && (
                  <Avatar>
                    <img src={avatar} alt={author} />
                  </Avatar>
                )}
                {isFetching ? (
                  <Bones minWidth={200} maxWidth={500} />
                ) : (
                  <Text typography="tiny" color="white">
                    {authorUrl ? (
                      <Button as="a" styleType="softLink" href={authorUrl}>
                        {author}
                      </Button>
                    ) : (
                      <>{author}</>
                    )}
                  </Text>
                )}
              </Grid>
              <LastUpdated />
            </Grid>
          </TextHeader>
        </ImageHeader>
        <DescriptionWrapper data-cy-id="community-idea-description">
          {isFetching ? (
            <>
              <Bones maxWidth={500} marginBottom="x2" />
              <Bones maxWidth={280} />
            </>
          ) : (
            <Description safeHTML={safeHTML} />
          )}
        </DescriptionWrapper>
      </Wrapper>
    </Track>
  );
};

export default Header;
